import React, { useCallback, useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Stack,
  Tooltip,
  IconButton,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpen from '@mui/icons-material/MenuOpen';
import ViewInAr from '@mui/icons-material/ViewInAr';
import Domain from '@mui/icons-material/Domain';
import Forum from '@mui/icons-material/Forum';
import Handshake from '@mui/icons-material/Handshake';
import PriceChange from '@mui/icons-material/PriceChangeOutlined';
import Science from '@mui/icons-material/ScienceOutlined';
import Diversity3 from '@mui/icons-material/Diversity3';
import Memory from '@mui/icons-material/Memory';
import Speed from '@mui/icons-material/Speed';
import PhoneForwarded from '@mui/icons-material/PhoneForwardedOutlined';
import {
  Storefront,
  Fingerprint,
  History,
  Logout,
  ViewModule,
  Payments,
  PhoneAndroid,
  Business,
  Quiz,
  VideoLibrary,
  ConfirmationNumber,
  Hub,
} from '@mui/icons-material';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useConsoleAuth } from '../hooks/useConsoleAuth';

const LARGHEZZA_APERTA = 240;
/**
 * ⚠️ 64 e non meno: l'icona sta al centro con un bersaglio da 44px attorno. Una barra
 * più stretta guadagna una manciata di pixel e regala un bottone che si sbaglia.
 */
const LARGHEZZA_CHIUSA = 64;
const MEMORIA = 'console.barra.chiusa';

/** La scorciatoia per stringere e allargare il menu: ⌘B sul Mac, Ctrl+B altrove (come la barra laterale di VS Code). */
const SU_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const TASTI_SCORCIATOIA = SU_MAC ? '⌘B' : 'Ctrl+B';

/**
 * Le voci del menu, come dati (i nomi sono chiavi del dizionario `menu`).
 *
 * ⚠️ **Le sezioni non sono decorazione: raggruppano per mestiere.** Quando la barra si
 * stringe le parole non entrano più, ma il raggruppamento resta un'informazione vera —
 * perciò diventa un filetto fra i gruppi invece di sparire. Buttare via la separazione
 * insieme alle parole trasformerebbe undici voci ordinate in un elenco piatto.
 */
const SEZIONI = [
  {
    titolo: 'piattaforma',
    voci: [
      { a: '/tenants', nome: 'tenants', icona: <Storefront fontSize="small" /> },
      { a: '/catalog', nome: 'catalogo', icona: <ViewModule fontSize="small" /> },
      { a: '/anagrafe', nome: 'anagrafe', icona: <Fingerprint fontSize="small" /> },
      { a: '/pricing', nome: 'pricing', icona: <Payments fontSize="small" /> },
      { a: '/consumi', nome: 'consumi', icona: <Speed fontSize="small" /> },
      { a: '/consegne', nome: 'consegne', icona: <PhoneForwarded fontSize="small" /> },
      { a: '/motori-ai', nome: 'motori_ai', icona: <Memory fontSize="small" /> },
      // La raccolta dei permessi di costruire dagli albi dei comuni (30/10/2026): salute degli albi, documenti non letti.
      { a: '/permessi', nome: 'permessi', icona: <Domain fontSize="small" /> },
      { a: '/forniture', nome: 'fornitori', icona: <Handshake fontSize="small" /> },
      { a: '/listini-fornitori', nome: 'listini_fornitori', icona: <PriceChange fontSize="small" /> },
      { a: '/app-mobile', nome: 'app_mobile', icona: <PhoneAndroid fontSize="small" /> },
      { a: '/apparecchi-ar', nome: 'realta_aumentata', icona: <ViewInAr fontSize="small" /> },
      { a: '/documentazione', nome: 'documentazione', icona: <VideoLibrary fontSize="small" /> },
      { a: '/audit', nome: 'audit', icona: <History fontSize="small" /> },
    ],
  },
  {
    titolo: 'acquisizione',
    voci: [
      { a: '/aziende', nome: 'aziende', icona: <Business fontSize="small" /> },
      { a: '/questionari', nome: 'questionari', icona: <Quiz fontSize="small" /> },
      // Le conversazioni dei visitatori di yumaos.com con Yuma (30/10/2026): da lì nascono iscrizioni.
      { a: '/yuma-sito', nome: 'yuma_sito', icona: <Forum fontSize="small" /> },
      // Far provare centralino e chatbot a chi è interessato (cap. 489, 04/11/2026).
      { a: '/prove', nome: 'prove', icona: <Science fontSize="small" /> },
      // Il programma partner (19/10/2026): «Partner» si scrive uguale in italiano e in inglese.
      { a: '/partner', nome: 'partner', icona: <Diversity3 fontSize="small" /> },
    ],
  },
  {
    titolo: 'personale',
    voci: [{ a: '/rete', nome: 'rete', icona: <Hub fontSize="small" /> }],
  },
  {
    titolo: 'assistenza',
    voci: [{ a: '/ticket', nome: 'ticket', icona: <ConfirmationNumber fontSize="small" /> }],
  },
] as const;

/** Vero se il tasto è stato premuto scrivendo in un campo: lì la scorciatoia non deve scattare. */
function staScrivendo(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null;
  return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
}

interface Props {
  children: React.ReactNode;
}

export const ConsoleLayout: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('menu');
  const { user } = useConsoleAuth();
  const theme = useTheme();
  /*
    TELEFONO (18/10/2026, Simone: «ottimizzare l'app della console anche per mobile», ora che si installa come
    YumaCONSOLE). Sotto i 900 px la barra fissa si mangiava metà schermo: diventa un menu a scomparsa che si apre
    dal bottone in alto a sinistra e si richiude da solo quando si sceglie una voce.
  */
  const telefono = useMediaQuery(theme.breakpoints.down('md'));
  const [menuAperto, setMenuAperto] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    setMenuAperto(false);
  }, [pathname]);

  /*
    ⚠️ La scelta si ricorda. Una barra che si riapre a ogni visita è una barra che
    nessuno stringe: chi lavora su una tabella larga la chiude una volta e si aspetta di
    ritrovarla così — rifare il gesto ogni volta costa più di quanto renda.
  */
  const [chiusa, setChiusa] = useState(() => {
    try {
      return localStorage.getItem(MEMORIA) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(MEMORIA, chiusa ? '1' : '0');
    } catch {
      /* modalità privata: la barra funziona, solo non si ricorda */
    }
  }, [chiusa]);

  // La scorciatoia da tastiera (solo sul computer, mai mentre si scrive in un campo).
  useEffect(() => {
    if (telefono) return;
    const tasto = (e: KeyboardEvent) => {
      if (e.code !== 'KeyB' || !(SU_MAC ? e.metaKey : e.ctrlKey) || e.altKey || e.shiftKey || staScrivendo(e)) return;
      e.preventDefault();
      setChiusa((v) => !v);
    };
    window.addEventListener('keydown', tasto);
    return () => window.removeEventListener('keydown', tasto);
  }, [telefono]);

  // Sul telefono il menu è sempre largo: si apre sopra la pagina, non le ruba spazio.
  const stretta = chiusa && !telefono;
  const larghezza = stretta ? LARGHEZZA_CHIUSA : LARGHEZZA_APERTA;

  const handleLogout = useCallback(async () => {
    await signOut(auth);
    navigate('/login');
  }, [navigate]);

  /** Una voce: icona sempre, parola quando c'è spazio, suggerimento quando non c'è. */
  const Voce = ({
    icona,
    nome,
    ...resto
  }: {
    icona: React.ReactNode;
    nome: string;
    [k: string]: unknown;
  }) => {
    const bottone = (
      <ListItemButton
        {...resto}
        sx={{
          minHeight: 44,
          px: stretta ? 0 : 2,
          justifyContent: stretta ? 'center' : 'flex-start',
        }}
      >
        <ListItemIcon sx={{ minWidth: stretta ? 0 : 36, justifyContent: 'center' }}>
          {icona}
        </ListItemIcon>
        {/*
          ⚠️ La parola si smonta, non si rimpicciolisce. Un testo lasciato lì a larghezza
          zero occupa comunque il suo posto nel calcolo del flex, e la riga resta storta
          per tutta la transizione.
        */}
        {!stretta && (
          <ListItemText
            primary={nome}
            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500, noWrap: true }}
          />
        )}
      </ListItemButton>
    );
    return stretta ? (
      <Tooltip title={nome} placement="right" arrow>
        {bottone}
      </Tooltip>
    ) : (
      bottone
    );
  };

  /*
    ⚠️ Il comando che stringe e allarga il menu sta IN ALTO A SINISTRA, nella barra scura, nello stesso punto del
    menu del telefono (27/10/2026, Simone: «il menu di sinistra della console sia comprimibile ed espandibile, con le
    classiche best practice»). Prima stava in fondo alla barra laterale, sotto sedici voci: su un portatile restava
    fuori dallo schermo e nessuno lo trovava. In alto è sempre visibile, è dove lo mettono le console di Google e i
    gestionali dello stesso genere, e lo stesso gesto vale su ogni schermo.
  */
  const etichettaComando = telefono ? t('barra.apri_menu') : chiusa ? t('barra.allarga') : t('barra.stringi');
  const suggerimentoComando = telefono
    ? t('barra.apri_menu')
    : chiusa
      ? t('barra.allarga_tasti', { tasti: TASTI_SCORCIATOIA })
      : t('barra.stringi_tasti', { tasti: TASTI_SCORCIATOIA });

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Sopra la barra fissa del computer, ma SOTTO il menu a scomparsa del telefono, che deve coprirla tutta. */}
      <AppBar position="fixed" sx={{ zIndex: (th) => (telefono ? th.zIndex.appBar : th.zIndex.drawer + 1) }}>
        <Toolbar sx={{ gap: 1.5, px: { xs: 1, sm: 1.5 } }}>
          <Tooltip title={suggerimentoComando}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => (telefono ? setMenuAperto(true) : setChiusa((v) => !v))}
              aria-label={etichettaComando}
              aria-expanded={telefono ? menuAperto : !chiusa}
              sx={{ width: 44, height: 44 }}
            >
              {telefono || chiusa ? <MenuIcon /> : <MenuOpen />}
            </IconButton>
          </Tooltip>
          {/* Brand mark — quadratino chiaro su slate scuro per dare identita'
              visiva senza dipendere da un asset esterno. */}
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              bgcolor: (th) => alpha(th.palette.primary.main, 0.85),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '0.75rem',
              color: '#fff',
              letterSpacing: '0.04em',
            }}
          >
            PW
          </Box>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600, lineHeight: 1, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            {t('barra.titolo')}
          </Typography>
          {/* Badge "platform mode" — discreto ma riconoscibile. Comunica
              all'operatore che sta usando la superficie privilegiata. */}
          <Chip
            label={t('barra.piattaforma')}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.62rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              bgcolor: (th) => alpha(th.palette.warning.main, 0.18),
              color: (th) => th.palette.warning.light,
              border: (th) => `1px solid ${alpha(th.palette.warning.main, 0.35)}`,
              '& .MuiChip-label': { px: 0.9 },
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Typography
            variant="caption"
            sx={{
              opacity: 0.8,
              fontFamily: 'monospace',
              fontSize: '0.72rem',
              display: { xs: 'none', sm: 'inline' },
            }}
          >
            {user?.email}
          </Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={telefono ? 'temporary' : 'permanent'}
        open={telefono ? menuAperto : true}
        onClose={() => setMenuAperto(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          // ⚠️ Solo la barra fissa occupa posto nella pagina: quella a scomparsa è uno strato sopra, e una larghezza
          // data alla sua radice restringerebbe anche il velo scuro che la chiude toccando fuori.
          ...(telefono ? {} : { width: larghezza, flexShrink: 0 }),
          '& .MuiDrawer-paper': {
            width: larghezza,
            boxSizing: 'border-box',
            overflow: 'hidden',
            /*
              ⚠️ La transizione si spegne per chi ha chiesto meno animazioni. Una barra
              che scorre è innocua per la maggior parte delle persone e non per tutte:
              rispettare la preferenza del sistema non costa niente.
            */
            transition: 'width .18s ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          },
        }}
      >
        {/* Sul telefono il menu copre la barra in alto: lo spazio vuoto lasciato per lei non serve. */}
        {!telefono && <Toolbar />}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0 }}>
          {/*
            Le voci scorrono da sole; «Esci» e la versione restano fermi in fondo. Con un solo scorrimento per tutta la
            barra, su uno schermo basso il fondo spariva insieme a quello che c'era.
          */}
          <Box component="nav" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', py: 1.5, overscrollBehavior: 'contain' }}>
            {SEZIONI.map((sezione, i) => (
              <React.Fragment key={sezione.titolo}>
                {stretta ? (
                  i > 0 && <Divider sx={{ mx: 1.5, my: 0.75 }} />
                ) : (
                  <Typography
                    variant="overline"
                    component="div"
                    sx={{
                      px: 3,
                      pb: 0.5,
                      pt: i > 0 ? 1 : 0,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      color: 'text.secondary',
                    }}
                  >
                    {t(`sezioni.${sezione.titolo}`)}
                  </Typography>
                )}
                <List sx={{ py: stretta ? 0.25 : 0.5 }}>
                  {sezione.voci.map((v) => (
                    <Voce key={v.a} component={NavLink} to={v.a} icona={v.icona} nome={t(`voci.${v.nome}`)} />
                  ))}
                </List>
              </React.Fragment>
            ))}
          </Box>

          {/* Il piede: sempre in vista, staccato dalle voci da un filetto */}
          <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', pt: 0.5 }}>
            <List sx={{ py: 0 }}>
              <Voce onClick={handleLogout} icona={<Logout fontSize="small" />} nome={t('piede.esci')} />
            </List>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={stretta ? 'center' : 'flex-start'}
              spacing={0.75}
              sx={{ px: stretta ? 0 : 3, pt: 1, pb: 1.5, opacity: 0.7, minWidth: 0 }}
            >
              <Tooltip title={stretta ? t('piede.versione') : ''} placement="right" arrow>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
              {/*
                ⚠️ `minWidth: 0` con `display: block`: senza, un contenitore elastico non
                scende mai sotto il suo contenuto e il testo esce dalla barra invece di
                troncare. È l'accoppiata che rompe i piè di pagina delle barre laterali.
              */}
              {!stretta && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    fontSize: '0.68rem',
                    color: 'text.secondary',
                    minWidth: 0,
                    display: 'block',
                  }}
                >
                  {t('piede.versione')}
                </Typography>
              )}
            </Stack>
          </Box>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: 'background.default', minWidth: 0 }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};
